import { useState, type FormEvent } from "react";
import { apiPost, type Incident } from "../api";
import { Icons } from "./icons";
import { useServiceSlugMap } from "../store/services";

interface Props {
  onClose: () => void;
  prefilled?: { correlation_id?: string; service_ids?: string[] };
}

const SEVERITIES = [
  { value: "informational", label: "Informativo" },
  { value: "minor", label: "Menor" },
  { value: "major", label: "Mayor" },
  { value: "critical", label: "Crítico" },
];

export function IncidentModal({ onClose, prefilled }: Props) {
  const slugMap = useServiceSlugMap();
  const [title, setTitle] = useState("");
  const [severity, setSeverity] = useState("minor");
  const [messagePublic, setMessagePublic] = useState("");
  const [messageInternal, setMessageInternal] = useState("");
  // `prefilled.service_ids` viene como uuids desde el contexto de la traza.
  // Resolvemos a slugs porque eso es lo que el usuario espera tipear/editar.
  const [services, setServices] = useState(
    (prefilled?.service_ids ?? []).map((id) => slugMap[id] ?? id).join(", "),
  );
  const [notify, setNotify] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        title,
        severity,
        message_public: messagePublic,
        message_internal: messageInternal || undefined,
        notify_subscribers: notify,
        affected_service_ids: services.split(",").map((s) => s.trim()).filter(Boolean),
      };

      if (prefilled?.correlation_id) {
        await apiPost<Incident>("/v1/incidents/from-trace", { ...payload, correlation_id: prefilled.correlation_id });
      } else {
        await apiPost<Incident>("/v1/incidents", payload);
      }

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error desconocido");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal modal-lg" onClick={(e) => e.stopPropagation()} onSubmit={onSubmit}>
        <div className="modal-head">
          <div>
            <h2>Nuevo incidente</h2>
            <div className="text-sm text-muted" style={{ marginTop: 2 }}>
              Notificá a los suscriptores con un mensaje público; el interno lo ve sólo el equipo.
            </div>
          </div>
          <button type="button" className="btn btn-icon btn-ghost" onClick={onClose}>
            <Icons.Close size={14} />
          </button>
        </div>

        <div className="modal-body" style={{ display: "grid", gap: 14 }}>
          <div>
            <label className="label">Título</label>
            <input
              className="input"
              placeholder="Errores 5xx intermitentes en pasarela de pagos"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 12 }}>
            <div>
              <label className="label">Severidad</label>
              <select className="select" value={severity} onChange={(e) => setSeverity(e.target.value)}>
                {SEVERITIES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Servicios afectados</label>
              <input
                className="input"
                placeholder="pagos.mercadopago, identidad.curp_nl"
                value={services}
                onChange={(e) => setServices(e.target.value)}
              />
              <div className="hint">Separados por coma.</div>
            </div>
          </div>

          <div>
            <label className="label">Mensaje público (markdown)</label>
            <textarea
              className="textarea"
              rows={3}
              placeholder="Estamos investigando errores intermitentes en pagos. Mitigación en curso."
              value={messagePublic}
              onChange={(e) => setMessagePublic(e.target.value)}
            />
          </div>

          <div>
            <label className="label">Mensaje interno (opcional)</label>
            <textarea
              className="textarea"
              rows={3}
              placeholder="Proveedor: latencia >5s desde 14:18 UTC. Caso abierto #4912."
              value={messageInternal}
              onChange={(e) => setMessageInternal(e.target.value)}
            />
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              style={{ accentColor: "var(--accent)" }}
            />
            Notificar a los suscriptores por email
          </label>

          {error && (
            <div className="login-error" role="alert">{error}</div>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Creando…" : "Crear incidente"}
          </button>
        </div>
      </form>
    </div>
  );
}
